import React from 'react';
import { CheckCircle2, KeyRound, Plus, Trash2 } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { CreatableSelect } from '../../ui/CreatableSelect';
import { ChoiceGroup } from '../ChoiceGroup';
import { useTrainingCourses } from '../../../hooks/useTrainingCourses';
import { usePractices } from '../../../hooks/usePractices';
import { practicesStore } from '../../../services/practicesStore';
import { practiceDeliveryModes } from '../../../data/practiceOptions';
import { formatDateTime, isInPresence, newId } from '../../../utils/practices';
import { PracticeCourseStatus, PracticeDeliveryMode, PracticeDraft, PracticeErrors, PracticeParticipant } from '../../../types/practice';
interface PracticeSchedulingProps {
  draft: PracticeDraft;
  onChange: (changes: Partial<PracticeDraft>) => void;
  errors: PracticeErrors;
  onConfirm: () => void;
}
export function PracticeScheduling({
  draft,
  onChange,
  errors,
  onConfirm
}: PracticeSchedulingProps) {
  const courses = useTrainingCourses();
  const {
    extraCourseCodes
  } = usePractices();
  const inPresence = isInPresence(draft);
  const confirmed = draft.schedulingConfirmed;

  // In-presence: auto-populated from the courses already inserted
  const presenceCodes = Array.from(new Set([...courses.filter(c => c.status !== 'ondemand').map(c => c.courseCode), ...extraCourseCodes]));
  // On-demand: the course code already exists on the delivery platform
  const elearningOptions = courses.filter(c => c.status === 'ondemand').map(c => ({
    value: c.courseCode,
    label: `${c.courseCode} — ${c.name}`
  }));

  // Keep the assigned course if it fits the chosen type of course
  const setMode = (mode: PracticeDeliveryMode) => {
    const current = courses.find(c => c.courseCode === draft.courseCode);
    const fits = current ? current.status === 'ondemand' === (mode === 'elearning') : mode !== 'elearning';
    onChange({
      deliveryMode: mode,
      courseCode: fits ? draft.courseCode : undefined,
      schedulingConfirmed: false,
      courseStatus: mode === 'elearning' ? 'On-demand' : 'Programmato'
    });
  };
  const updateParticipant = (id: string, changes: Partial<PracticeParticipant>) => onChange({
    participants: draft.participants.map(p => p.id === id ? {
      ...p,
      ...changes
    } : p)
  });
  const addParticipant = () => onChange({
    participants: [...draft.participants, {
      id: newId('p'),
      firstName: '',
      lastName: '',
      email: ''
    }]
  });
  const removeParticipant = (id: string) => onChange({
    participants: draft.participants.filter(p => p.id !== id)
  });
  const sendCredentials = () => {
    const now = new Date().toISOString();
    onChange({
      participants: draft.participants.map(p => p.credentialsSentAt || !p.email ? p : {
        ...p,
        username: `${p.firstName.charAt(0)}.${p.lastName}`.toLowerCase().replace(/\s+/g, ''),
        credentialsSentAt: now
      })
    });
  };
  const pendingCredentials = draft.participants.filter(p => !p.credentialsSentAt).length;
  return <div className="space-y-6" data-id="element-1665" data-mp="anaoipawabpw">
      <ChoiceGroup label="Tipo di corso" name="practice-mode" value={draft.deliveryMode || '' as PracticeDeliveryMode} options={practiceDeliveryModes} onChange={setMode} data-id="element-1666" data-mp="ao3l4xg53kub" />
      {errors.deliveryMode && <p className="text-xs text-red-600 font-medium -mt-4" data-id="element-1667" data-mp="omjuyx9atrgo">
          {errors.deliveryMode}
        </p>}

      {draft.deliveryMode && <fieldset disabled={confirmed} className="space-y-6 disabled:opacity-80" data-id="element-1668" data-mp="1v1ueykzt66qb">
          {inPresence ? <>
              <div data-id="element-1669" data-mp="1yk0ec8cy73m2">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1670" data-mp="1yji9wog4tj32">
                  1. Calendarizzare il corso
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1671" data-mp="1tmi1w7zq269b">
                  <Input label="Data inizio" type="date" value={draft.courseStartDate || ''} onChange={e => onChange({
              courseStartDate: e.target.value || undefined
            })} error={errors.courseStartDate} required data-id="element-1672" data-mp="27lrgj38a4sb6" />
                  <Input label="Data fine" type="date" min={draft.courseStartDate} value={draft.courseEndDate || ''} onChange={e => onChange({
              courseEndDate: e.target.value || undefined
            })} error={errors.courseEndDate} data-id="element-1673" data-mp="2r8yy19wpydth" />
                </div>
              </div>
              <div data-id="element-1674" data-mp="3etzt7s6p2ez6">
                <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-1675" data-mp="3jv5mps86i69r">
                  2. Codice Corso
                </p>
                <p className="text-xs text-slate-500 mb-3" data-id="element-1676" data-mp="35vxop2nr5475">
                  Il corso viene creato manualmente sulla piattaforma esterna di
                  erogazione.
                </p>
                <CreatableSelect label="Codice Corso" value={draft.courseCode || ''} onChange={v => onChange({
            courseCode: v || undefined
          })} options={presenceCodes} onCreate={v => practicesStore.addCourseCode(v)} placeholder="Seleziona dai corsi già inseriti..." newPlaceholder="Inserisci nuovo codice corso" newOptionLabel="+ Inserisci nuovo codice corso..." error={errors.courseCode} required data-id="element-1677" data-mp="2w52yjn7dxkgf" />
              </div>
              <div data-id="element-1678" data-mp="npv7kdvdk8wt">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1679" data-mp="nr6721hseui4">
                  3. Dati del corso
                </p>
                <Textarea label="Dati del corso" rows={3} value={draft.courseDetails || ''} onChange={e => onChange({
            courseDetails: e.target.value
          })} placeholder="Es. luogo, orari, docente, materiale..." data-id="element-1680" data-mp="ix1zbg0p6ifz" />
              </div>
            </> : <>
              <SearchableSelect label="Codice Corso" value={draft.courseCode || ''} onChange={e => onChange({
          courseCode: e.target.value
        })} options={elearningOptions} placeholder="Cerca il corso già presente sulla piattaforma..." description="Per i corsi On-demand il codice è già presente sulla piattaforma esterna di erogazione" error={errors.courseCode} required data-id="element-1681" data-mp="11q5ux6xh75d8" />
              <div data-id="element-1682" data-mp="1sus19tn2azh1">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1683" data-mp="1xvz9ftt4a0o8">
                  1. Dati dei partecipanti
                </p>
                <div className="space-y-3" data-id="element-1684" data-mp="21wi0hln6r2pd">
                  {draft.participants.map(p => <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-id="element-1685" data-mp="26pb4u8eqs90x">
                      <div className="md:col-span-3" data-id="element-1686" data-mp="2gm75jryefqux">
                        <Input aria-label="Nome partecipante" placeholder="Nome" value={p.firstName} onChange={e => updateParticipant(p.id, {
                  firstName: e.target.value
                })} disabled={Boolean(p.credentialsSentAt)} data-id="element-1687" data-mp="2gkw25t8v8l4c" />
                      </div>
                      <div className="md:col-span-3" data-id="element-1688" data-mp="ccc9vpngqz11">
                        <Input aria-label="Cognome partecipante" placeholder="Cognome" value={p.lastName} onChange={e => updateParticipant(p.id, {
                  lastName: e.target.value
                })} disabled={Boolean(p.credentialsSentAt)} data-id="element-1689" data-mp="cb18txlek5dm" />
                      </div>
                      <div className="md:col-span-4" data-id="element-1690" data-mp="3kl2r4t4kfrem">
                        <Input aria-label="Email partecipante" type="email" placeholder="Email" value={p.email} onChange={e => updateParticipant(p.id, {
                  email: e.target.value
                })} disabled={Boolean(p.credentialsSentAt)} data-id="element-1691" data-mp="3kmdreiysyh0z" />
                      </div>
                      <div className="md:col-span-2 flex items-center justify-between gap-2 md:pt-2" data-id="element-1692" data-mp="1hahaxnb3kxmq">
                        {p.credentialsSentAt ? <span className="text-xs text-green-700 flex items-center gap-1" title={`Utente ${p.username} · inviate il ${formatDateTime(p.credentialsSentAt)}`} data-id="element-1693" data-mp="17ee96ik5r10b">
                            <CheckCircle2 className="h-4 w-4" data-id="element-1694" data-mp="204midkwpub2r" /> Inviate
                          </span> : <span className="text-xs text-slate-400" data-id="element-1695" data-mp="1qcyz47abc0cd">
                            Da inviare
                          </span>}
                        <Button type="button" variant="light" color="danger" size="sm" className="!px-2" aria-label="Rimuovi partecipante" onClick={() => removeParticipant(p.id)} data-id="element-1696" data-mp="1v97790q3eta1">
                          <Trash2 className="h-4 w-4" data-id="element-1697" data-mp="1sqvbly1ybfal" />
                        </Button>
                      </div>
                    </div>)}
                </div>
                {errors.participants && <p className="text-xs text-red-600 font-medium mt-2" data-id="element-1698" data-mp="153dfxrfkfx2c">
                    {errors.participants}
                  </p>}
                <Button type="button" size="sm" variant="flat" color="secondary" className="mt-3" startContent={<Plus className="h-4 w-4" data-id="element-1700" data-mp="104tq3fxuq14p" />} onClick={addParticipant} data-id="element-1699" data-mp="19voeoqyklctb">
                  Aggiungi partecipante
                </Button>
              </div>
              <div data-id="element-1701" data-mp="r2aspbcu4gpu">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1702" data-mp="r2uckxm1pf6i">
                  2. Credenziali di accesso
                </p>
                <Button type="button" variant="flat" color="secondary" startContent={<KeyRound className="h-4 w-4" data-id="element-1704" data-mp="hgrmb8g5nsti" />} onClick={sendCredentials} disabled={pendingCredentials === 0} data-id="element-1703" data-mp="2izaaq9thmk2">
                  Genera e invia credenziali
                  {pendingCredentials > 0 && ` (${pendingCredentials})`}
                </Button>
                {errors.credentials && <p className="text-xs text-red-600 font-medium mt-2" data-id="element-1705" data-mp="106hpaiu2nkwi">
                    {errors.credentials}
                  </p>}
              </div>
            </>}
        </fieldset>}

      {draft.deliveryMode && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-5 border-t border-slate-100" data-id="element-1706" data-mp="197pca4r50zba">
          <div className="flex items-center gap-4" data-id="element-1707" data-mp="153hpwe85jvg8">
            {inPresence ? <div className="w-48" data-id="element-1708" data-mp="1calszt1jfbal">
                <Select label="Stato del corso" value={draft.courseStatus || 'Programmato'} onChange={e => onChange({
            courseStatus: e.target.value as PracticeCourseStatus
          })} options={[{
            value: 'Programmato',
            label: 'Programmato'
          }, {
            value: 'Eseguito',
            label: 'Eseguito'
          }]} data-id="element-1709" data-mp="1h7lz23ye0e1p" />
              </div> : <div data-id="element-1710" data-mp="24xzgz1n9cwik">
                <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1711" data-mp="24vd9f20dah0c">
                  Stato del corso
                </span>
                <span className="inline-flex rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-seform-navy" data-id="element-1712" data-mp="ajyiwizj8djj">
                  On-demand
                </span>
              </div>}
          </div>
          {confirmed ? <div className="flex items-center gap-3" data-id="element-1713" data-mp="2sucmztiuidg4">
              <span className="text-sm font-medium text-green-700 flex items-center gap-1.5" data-id="element-1714" data-mp="2o1jecuk9akyr">
                <CheckCircle2 className="h-4 w-4" data-id="element-1715" data-mp="2jzy6xoqhqyvy" /> Programmazione confermata
              </span>
              <Button type="button" size="sm" variant="flat" color="default" onClick={() => onChange({
          schedulingConfirmed: false
        })} data-id="element-1716" data-mp="2f1wp174sx5m2">
                Modifica
              </Button>
            </div> : <Button type="button" onClick={onConfirm} className="sm:min-w-[120px]" data-id="element-1717" data-mp="32q66jwuzf77e">
              OK
            </Button>}
        </div>}
    </div>;
}